<script lang="ts" setup>
import { Mask } from 'maska'
import type { BaseValidation } from '@vuelidate/core'
import { InputHTMLAttributes } from 'nuxt/dist/app/compat/capi'

const props = defineProps<{
  label?: string
  inputElAttrs?: InputHTMLAttributes
  modelValue?: string
  validation?: BaseValidation
  lowerCase?: boolean
  upperCase?: boolean
  mask?: string
  disabled?: boolean
  readonly?: boolean
  loading?: boolean
  applyRegex?: RegExp
  maxlength?: number
}>()

const emit = defineEmits(['update:modelValue'])
const slots = useSlots()

const inputEl = ref()
const theme = useStoreTheme()
const mask = computed(() => new Mask({ mask: props.mask }))

const updateValue = (e: Event) => {
  let val = (e.target as HTMLInputElement).value

  if (props.applyRegex) {
    val = val.replace(props.applyRegex, '')
    inputEl.value.value = val
  }

  let formattedVal: string = val

  if (props.lowerCase) {
    formattedVal = val.toLowerCase()
  } else if (props.upperCase) {
    formattedVal = val.toUpperCase()
  }

  if (props.mask) {
    inputEl.value.value = mask.value.masked(formattedVal)
    emit('update:modelValue', mask.value.unmasked(formattedVal))
  } else {
    emit('update:modelValue', formattedVal)
  }

  props.validation?.$touch()
}

const shouldDisplayErrorMessage = computed(() => {
  return props.validation?.$invalid && props.validation?.$dirty
})

const value = computed(() => {
  if (props.mask) return mask.value.masked(props.modelValue || '')

  return props.modelValue
})

defineExpose({
  inputEl,
})
</script>

<template>
  <div class="input-field flex w-full flex-col gap-2">
    <div v-if="props.label" class="text-sm font-medium text-gray-350">{{ props.label }}</div>

    <div>
      <div class="relative" :class="{ flex: $slots['prepended-input'] }">
        <slot name="prepended-input" />

        <div
          v-if="slots['prepended-icon']"
          class="prepended-icon absolute left-4 flex h-12 items-center justify-center"
          :class="{
            'text-gray-300': !props.modelValue,
          }"
        >
          <slot name="prepended-icon" />
        </div>

        <input
          ref="inputEl"
          :value="value"
          class="input-el h-12 w-full rounded-full border-1 border-gray-200 px-6 py-3 text-base outline-[1.5px] transition-all placeholder:text-gray-300 focus:outline sm:text-sm"
          :class="{
            'border-red-700': shouldDisplayErrorMessage,
            'rounded-l-none border-l-0 !px-4': $slots['prepended-input'],
            'cursor-not-allowed border-none border-transparent bg-gray-200 text-gray-300':
              props.disabled,
            'text-gray-400': !props.disabled,
            '!pl-[42px]': slots['prepended-icon'],
          }"
          :style="{ outlineColor: theme.primary }"
          v-bind="props.inputElAttrs"
          :readonly="props.loading || props.readonly"
          :disabled="disabled"
          :maxlength="props.maxlength"
          @input="updateValue"
        />
        <RIcon
          v-if="props.loading"
          size="22"
          name="svg-spinners:180-ring"
          class="spinner-icon absolute bottom-3 right-6 top-3"
        />
      </div>

      <div v-if="shouldDisplayErrorMessage" class="mt-2 text-[12px] text-red-700">
        {{ props.validation?.$errors?.[0]?.$message }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.spinner-icon {
  color: var(--color-primary);
}
</style>
